import React, { useMemo } from 'react';
import { Maybe, Offer, SaleCalendar } from '@gql/schema';
import { i18n } from '@secretescapes/i18n';
import { getFormattedPriceWithBookingFee } from '@utils/pricing.utils';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useTranslations } from '@utils/useTranslations';
import { TWithFlightsDescription, TWithFlightsOffers } from '@features/Flights';
import { RetryBox } from '@features/Sale/RetryBox/RetryBox';
import { getFinalRates } from '../AvailabilityPresentation.utils';
import { useSelectionState } from '../SelectionContext';
import { OfferCard } from './OfferCard';
import { Grid } from './Offers.styles';
import {
  getInfoBoxType,
  isBookable,
  isPackageAndTravellersMatch,
  sortOffers,
} from './Offers.utils';

interface Props {
  offers: Offer[];
  onBookButtonClicked: (offerId: string) => void;
  numberOfRooms: number;
  openCalendar: (offerId: string) => void;
  flightsInfo: Maybe<{
    withFlightsDescription: TWithFlightsDescription;
    withFlightsOffers: TWithFlightsOffers;
  }>;
  noAvailabilityReturned: boolean;
  isLoading: boolean;
  availability: Maybe<SaleCalendar>;
}

export const Offers = ({
  offers,
  availability,
  onBookButtonClicked,
  numberOfRooms,
  openCalendar,
  flightsInfo,
  noAvailabilityReturned,
  isLoading,
}: Props) => {
  const { territoryLocale, currency, isMobile } = useApplicationState();
  const { finalRates } = useAppState();
  const { travellersSelection } = useSelectionState();
  const { translations } = useTranslations();

  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);
  const offerCardDetailsEnabled = useFeatureFlag(BullfinchFeatureFlags.offerCardDetailsEnabled);
  const travellersHOSelectionEnabled = useFeatureFlag(BullfinchFeatureFlags.travellersHOSelection);
  const isRelevantTravellersSelectionEnabled =
    !!flightsInfo || (!flightsInfo && travellersHOSelectionEnabled);

  const { withFlightsDescription, withFlightsOffers } = flightsInfo ?? {};

  const hasNoFlights = withFlightsOffers && Object.keys(withFlightsOffers).length === 0;

  const determineInfoBox = (offer: Offer) => {
    return getInfoBoxType(
      finalRates.show,
      numberOfRooms,
      offer,
      noAvailabilityReturned,
      isRelevantTravellersSelectionEnabled ? travellersSelection : undefined
    );
  };

  const sortedOffers = sortOffers(offers, determineInfoBox);

  const showPhotos = useMemo(() => offers.every(offer => offer?.room?.photos?.length), [offers]);

  if (isLoading) {
    return (
      <Skeleton
        numberOfCards={offers.length}
        showOfferCardButton={finalRates.show}
        showDetailsContent={!offerCardDetailsEnabled}
        showPhoto={showPhotos}
      />
    );
  }

  if (hasNoFlights) {
    return (
      <RetryBox
        retryFunction={() => {
          setURLParams('tripType', 'hotelOnly');
          window?.location?.reload();
        }}
        subtitle={i18n('sale.noflights.text', [], translations)}
        buttonText={i18n('sale.navigate.hotelonly', [], translations)}
        isMobile={isMobile}
      />
    );
  }

  return (
    <Grid>
      {sortedOffers.map(offer => {
        const { description, prices } = offer;

        const rates = getFinalRates(offer.receipt, numberOfRooms, currency, territoryLocale);

        const infoBoxType = determineInfoBox(offer);

        const showDisclaimer = !isPackageAndTravellersMatch(
          availability?.packagePriceBasis,
          isRelevantTravellersSelectionEnabled ? travellersSelection : undefined
        );

        // Apply booking fee if HO and FF on
        const leadRate = flightsInfo
          ? (withFlightsOffers?.[offer.id]?.price ?? '')
          : bookingFeesEnabled
            ? getFormattedPriceWithBookingFee(
                Math.ceil(prices?.leadRate?.unit ?? 0),
                currency,
                territoryLocale
              )
            : (prices?.leadRate?.forDisplay ?? '');

        return (
          <OfferCard
            key={offer.id}
            offer={offer}
            isRefundableCheckInDate={offer.receipt?.isRefundable ?? undefined}
            isBookable={isBookable(finalRates.show, offer.receipt)}
            onBookButtonClicked={onBookButtonClicked}
            finalRate={rates?.finalRate ?? ''}
            finalRatePerRoomPerNight={rates?.finalRatePerRoomPerNight ?? ''}
            availableRoomCount={offer.receipt?.maxAvailability ?? undefined}
            openCalendar={openCalendar}
            infoBoxType={isLoading ? null : infoBoxType}
            pricing={{
              leadRate,
              rackRate: prices?.rackRate?.forDisplay ?? '',
              showRackRate: !flightsInfo && !!prices?.pricingRules?.showRackRate,
              showDiscount:
                !flightsInfo && !!prices?.pricingRules?.showDiscount && !!prices.discount,
              showDisclaimer,
            }}
            description={
              flightsInfo
                ? (withFlightsDescription?.(offer.description ?? '') ?? '')
                : (description ?? '')
            }
            numberOfTravellers={finalRates.numberOfTravellers}
            numberOfNights={finalRates.numberOfNights}
            flightsSelected={!!flightsInfo}
            showPhotos={showPhotos}
            numberOfRooms={numberOfRooms}
          />
        );
      })}
    </Grid>
  );
};

interface OffersSkeletonProps {
  numberOfCards: number;
  showOfferCardButton?: boolean;
  showDetailsContent: boolean;
  showPhoto: boolean;
}

const Skeleton = ({
  numberOfCards,
  showOfferCardButton,
  showDetailsContent,
  showPhoto,
}: OffersSkeletonProps) => {
  return (
    <Grid>
      {Array.from({ length: numberOfCards }, (_, index) => (
        <OfferCard.Skeleton
          key={`skeleton-${index}`}
          showButton={!!showOfferCardButton}
          showDetailsContent={showDetailsContent}
          showPhoto={showPhoto}
        />
      ))}
    </Grid>
  );
};

Offers.Skeleton = Skeleton;
